/**
 * AutoQuoteEmbedPage - External Embed Page for One-click Quote Submission
 *
 * One-click quote submission page for Kakao alimtalk button.
 * Auto-generates and submits quote response from URL parameters.
 * Uses Embed Token (OTT) for authentication.
 *
 * Flow:
 * 1. Parse URL params (project, company, price, token)
 * 2. Validate supplier authentication via Embed Token
 * 3. Fetch quote request data
 * 4. Auto-submit quote response
 * 5. Redirect to chat room on success
 *
 * URL: /auto-quote?project={quoteRequestId}&company={supplierId}&price={unitPrice}#token={embedToken}
 * (legacy links may still carry &token= in the query during rollout)
 */

import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  EmbedLoadingState,
  EmbedErrorState,
  useVendorAuth,
} from "../features/auth";
import { useAutoQuoteSubmission } from "../features/auto-quote/presentation/hooks/useAutoQuoteSubmission";

export function AutoQuoteEmbedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse URL parameters
  const quoteRequestId = searchParams.get("project");
  // Note: supplierId from URL is secondary to supplierId from auth token, but we use URL one for initial context if needed
  const urlSupplierId = searchParams.get("company");
  const priceParam = searchParams.get("price");
  const unitPrice = priceParam ? parseFloat(priceParam) : 0;

  // ✅ 1. Token-based Authentication (Verified by EmbedAuthProvider wrapper)
  const { currentUser } = useVendorAuth();
  const authSupplierId = currentUser?.id;

  // Use authenticated supplierId if available, otherwise fallback to URL
  const activeSupplierId = authSupplierId || urlSupplierId || "";

  // Auto-submit quote
  const {
    submitQuote,
    isSubmitting,
    isFetching,
    error: submitError,
    chatRoomId,
  } = useAutoQuoteSubmission({
    quoteRequestId: quoteRequestId || "",
    unitPrice,
    supplierId: activeSupplierId,
  });

  // Redirect to chat room on success
  useEffect(() => {
    if (chatRoomId) {
      // Delay redirect slightly to show success message
      const timer = setTimeout(() => {
        // The single-use embed token is already exchanged and its session
        // persisted — internal navigation must not re-append it to the URL.
        navigate(`/embed/chat/${chatRoomId}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [chatRoomId, navigate]);

  // Auto-submit when authenticated and data loaded
  useEffect(() => {
    if (
      !isSubmitting &&
      !submitError &&
      !chatRoomId &&
      !isFetching &&
      activeSupplierId &&
      quoteRequestId
    ) {
      submitQuote();
    }
  }, [
    isSubmitting,
    submitError,
    chatRoomId,
    isFetching,
    submitQuote,
    activeSupplierId,
    quoteRequestId,
  ]);

  // Validation: Missing required parameters
  if (!quoteRequestId || !priceParam) {
    return (
      <EmbedErrorState
        title="잘못된 접근"
        message="필수 파라미터(project, price)가 누락되었습니다."
      />
    );
  }

  // Loading: Fetching quote request data
  if (isFetching) {
    return <EmbedLoadingState message="견적 요청 정보를 불러오는 중..." />;
  }

  // Loading: Submitting quote
  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-slate-50  flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600 ">견적을 제출하는 중...</p>
          <p className="text-sm text-slate-500 ">
            단가(공급가액): {unitPrice.toLocaleString()}원
          </p>
        </div>
      </div>
    );
  }

  // Success: Quote submitted, redirecting to chat
  if (chatRoomId) {
    return (
      <div className="min-h-screen bg-slate-50  flex items-center justify-center">
        <div className="text-center p-8 bg-white  rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100  rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600 "
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900  mb-2">
            견적 제출 완료
          </h2>
          <p className="text-slate-600 ">
            견적이 성공적으로 제출되었습니다.
            <br />
            채팅방으로 이동합니다...
          </p>
        </div>
      </div>
    );
  }

  // Error: Submission failed
  if (submitError) {
    return (
      <EmbedErrorState
        title="견적 제출 실패"
        message={submitError.message || "예상치 못한 오류가 발생했습니다"}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Default: Should not reach here due to auto-submit
  return <EmbedLoadingState message="처리 중..." />;
}
